<template>
  <v-dialog
    :value="value"
    max-width="600"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="text-subtitle-1">
        グループ管理
        <v-spacer />
        <v-btn
          icon
          @click="$emit('input', false)"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- グループ追加 -->
        <div class="d-flex align-center mb-4">
          <v-text-field
            v-model="newGroupName"
            placeholder="新しいグループ名"
            outlined
            dense
            hide-details
            class="mr-2"
            @keydown.enter="createGroup"
          />
          <v-btn
            color="primary"
            :disabled="!newGroupName.trim()"
            :loading="isCreating"
            @click="createGroup"
          >
            追加
          </v-btn>
        </div>

        <v-divider class="mb-4" />

        <!-- グループ一覧 -->
        <div
          v-if="groups.length === 0"
          class="text-center grey--text pa-4"
        >
          グループがありません
        </div>

        <v-list
          v-else
          dense
        >
          <template v-for="group in groups">
            <v-list-item :key="`group-${group.id}`">
              <v-list-item-content>
                <v-list-item-title v-if="editingGroupId !== parseInt(group.id, 10)">
                  {{ group.attributes.name }}
                  <span class="grey--text text-caption ml-1">
                    ({{ (group.attributes.users || []).length }}名)
                  </span>
                </v-list-item-title>
                <v-text-field
                  v-else
                  v-model="editingGroupName"
                  dense
                  hide-details
                  outlined
                  class="mt-0 pt-0"
                  @keydown.enter="saveGroupName(group)"
                  @keydown.esc="cancelEdit"
                />
              </v-list-item-content>
              <div class="d-flex align-center">
                <v-btn
                  v-if="editingGroupId === parseInt(group.id, 10)"
                  icon
                  small
                  @click="saveGroupName(group)"
                >
                  <v-icon small>
                    {{ icons.mdiCheck }}
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="editingGroupId === parseInt(group.id, 10)"
                  icon
                  small
                  @click="cancelEdit"
                >
                  <v-icon small>
                    {{ icons.mdiClose }}
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="editingGroupId !== parseInt(group.id, 10)"
                  icon
                  small
                  @click="startEdit(group)"
                >
                  <v-icon
                    small
                    color="grey darken-1"
                  >
                    {{ icons.mdiPencil }}
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="editingGroupId !== parseInt(group.id, 10)"
                  icon
                  small
                  @click="deleteGroup(group)"
                >
                  <v-icon
                    small
                    color="error"
                  >
                    {{ icons.mdiDelete }}
                  </v-icon>
                </v-btn>
              </div>
            </v-list-item>

            <!-- メンバー一覧 -->
            <div
              v-if="group.attributes.users && group.attributes.users.length > 0"
              :key="`members-${group.id}`"
              class="pl-8 pb-2"
            >
              <v-chip
                v-for="member in group.attributes.users"
                :key="member.id"
                small
                close
                class="mr-1 mb-1"
                @click:close="removeUserFromGroup(member)"
              >
                {{ member.name }}
              </v-chip>
            </div>

            <!-- ユーザー追加セレクト -->
            <div
              :key="`add-user-${group.id}`"
              class="pl-8 pb-3"
            >
              <v-select
                :items="availableUsersForGroup(group)"
                item-text="name"
                item-value="id"
                placeholder="ユーザーを追加"
                dense
                outlined
                hide-details
                clearable
                style="max-width: 250px"
                @change="addUserToGroup($event, group)"
              />
            </div>
          </template>
        </v-list>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiClose,
  mdiCheck,
  mdiPencil,
  mdiDelete,
} from '@mdi/js'
import CastGroupApi from '@/api/waiter/CastGroup'
import UserApi from '@/api/waiter/User'

export default {
  name: 'CastGroupManagementDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      groups: [],
      allUsers: [],
      newGroupName: '',
      isCreating: false,
      editingGroupId: null,
      editingGroupName: '',
      icons: {
        mdiClose,
        mdiCheck,
        mdiPencil,
        mdiDelete,
      },
    }
  },
  watch: {
    value(v) {
      if (v) this.fetchData()
    },
  },
  methods: {
    async fetchData() {
      try {
        const [groupsRes, usersRes] = await Promise.all([
          CastGroupApi.getCastGroups(),
          UserApi.getUsers(),
        ])
        this.groups = groupsRes.data?.castGroups?.data || []
        this.allUsers = (usersRes.data?.users?.data || [])
          .filter(u => u.attributes?.role === 'cast')
          .map(u => ({ id: parseInt(u.id, 10), name: u.attributes.name, castGroupId: u.attributes?.castGroupId }))
      } catch {
        // ignore
      }
    },
    availableUsersForGroup(group) {
      const memberIds = (group.attributes.users || []).map(u => u.id)

      return this.allUsers.filter(u => !u.castGroupId && !memberIds.includes(u.id))
    },
    async createGroup() {
      if (!this.newGroupName.trim()) return
      this.isCreating = true
      try {
        await CastGroupApi.createCastGroup({ name: this.newGroupName.trim(), position: this.groups.length })
        this.newGroupName = ''
        await this.fetchData()
        this.$emit('updated')
      } catch {
        // ignore
      } finally {
        this.isCreating = false
      }
    },
    startEdit(group) {
      this.editingGroupId = parseInt(group.id, 10)
      this.editingGroupName = group.attributes.name
    },
    cancelEdit() {
      this.editingGroupId = null
      this.editingGroupName = ''
    },
    async saveGroupName(group) {
      if (!this.editingGroupName.trim()) return
      try {
        await CastGroupApi.updateCastGroup(group.id, { name: this.editingGroupName.trim() })
        this.cancelEdit()
        await this.fetchData()
        this.$emit('updated')
      } catch {
        // ignore
      }
    },
    async deleteGroup(group) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`「${group.attributes.name}」を削除しますか？所属ユーザーのグループは解除されます。`)) return
      try {
        await CastGroupApi.deleteCastGroup(group.id)
        await this.fetchData()
        this.$emit('updated')
      } catch {
        // ignore
      }
    },
    async addUserToGroup(userId, group) {
      if (!userId) return
      try {
        await UserApi.updateUserCastGroup(userId, parseInt(group.id, 10))
        await this.fetchData()
        this.$emit('updated')
      } catch {
        // ignore
      }
    },
    async removeUserFromGroup(member) {
      try {
        await UserApi.updateUserCastGroup(member.id, null)
        await this.fetchData()
        this.$emit('updated')
      } catch {
        // ignore
      }
    },
  },
}
</script>
